import { useState, useEffect, useRef } from "react"
import { Box, Typography, TextField, Button, Stack, Link, Divider, Alert, IconButton, Grid } from '@mui/material'
import email from '../../assets/svg/Emailupdate.svg'
import mobile from '../../assets/svg/Mobileupdate.svg'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { useSelector } from "react-redux"
import { useAppData } from "../../redux/appDataProvider"
import ApiClient from "../../utilities/apiClient"
import AppConfig from "../../AppConfig"

const ExistingContactOtp = ({ maskedValue, fieldType, onNext, onClose }) => {
  const { userInfo, setUserInfo } = useAppData()

  const apiToken = useSelector(state => state.auth.apiToken)

  const [otp, setOtp] = useState(["", "", "", ""])
  const [secondsLeft, setSecondsLeft] = useState(30)
  const [resendCount, setResendCount] = useState(0)
  const [error, setError] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const inputRefs = useRef([])

  useEffect(() => {
    if (secondsLeft === 0 || resendCount > 2) return

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [secondsLeft, resendCount])

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleResendOtp = () => {

    setOtp(["", "", "", ""])
    setError(false)
    setErrorMsg("")

    if (resendCount < 2) {
      setSecondsLeft(30)
    }

    setResendCount(prev => prev + 1)

    const payload = {
      clientId: userInfo.CLIENT_ID,
      email: userInfo.CLIENT_ID_MAIL,
      mobileNumber: userInfo.MOBILE_NO,
      updateType: fieldType === 'email' ? 'email' : 'mobile',
      fullName: userInfo.CLIENT_NAME
    }

    try {

      const apiClient = new ApiClient(apiToken)

      apiClient
        .fetchData(AppConfig.api.Profile.sendOtp, "POST", payload)
        .then((response) => {
          if (!response) {
            console.error("Error: Response is missing.")
            setError(true)
            return
          }

          if (response.status === 200 && response.data.success) {
            console.log(response.data?.message)
          } else if ((response.status === 400 || response.status === 500) && !response.data.success) {

            if (response.data?.message?.includes("Maximum OTP")) {
              setErrorMsg(response.data.message)
            }

            setError(true)
            console.log("Error:", response.data?.message)
          } else {
            setError(true)
            console.warn("Unexpected response status:", response.status)
          }

        })
        .catch((error) => {
          console.error("Error during API request (Promise rejection):", error)
          setError(true)
        })
    } catch (error) {
      console.error("Error during API request (Try block) :", error)
      setError(true)
    }

  }

  const handleVerifyOtp = () => {

    const payload = {
      clientId: userInfo.CLIENT_ID,
      otpCode: otp.join("")
    }

    try {

      const apiClient = new ApiClient(apiToken)

      apiClient
        .fetchData(AppConfig.api.Profile.verifyOtp, "POST", payload)
        .then((response) => {
          if (!response) {
            console.error("Error: Response is missing.")
            setErrorMsg("Error in verify OTP. Please try later.")
            setError(true)
            return
          }

          if (response.status === 200 && response.data.success) {
            onNext()
            console.log(response.data?.message)
          } else if ((response.status === 400 || response.status === 404 || response.status === 500) && !response.data.success) {

            setErrorMsg(response.data.message)
            setError(true)
            console.log("Error:", response.data?.message)
          } else {
            setError(true)
            console.warn("Unexpected response status:", response.status)
          }

        })
        .catch((error) => {
          console.error("Error during API request (Promise rejection):", error)
          setErrorMsg("Error in verify OTP. Please try later.")
          setError(true)
        })
    } catch (error) {
      console.error("Error during API request (Try block) :", error)
      setErrorMsg("Error in verify OTP. Please try later.")
      setError(true)
    }

  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }} fontWeight="bold" mb={1}>
              Verify Request by {fieldType === 'email' ? 'Mobile' : 'Email'}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose}>
              <HighlightOffIcon sx={{ color: '#F3D45E' }} />
            </IconButton>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2, borderColor: "#999" }} />

        <Typography sx={{ color: "#888", mb: 3, fontSize: {xs: '14px', sm: '16px'} }}>Usually takes few seconds</Typography>

        <Box textAlign="center" mb={3}>
          <img src={fieldType === 'email' ? mobile : email} alt="OTP" width={125} />
          <Typography mt={2} sx={{ fontSize: {xs: '14px', sm: '16px'} }}>
            Verify request over existing {fieldType === 'email' ? 'Mobile Number' : 'Email ID'}
          </Typography>
          <Typography color="text.secondary" mt={2} sx={{ fontSize: {xs: '14px', sm: '16px'} }}>
            We have sent a 4 digit OTP on <br />
            <strong>{maskedValue}</strong>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="center" mb={2}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              value={digit}
              inputRef={(el) => (inputRefs.current[index] = el)}
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center", fontWeight: 'bold' }
              }}
              size="small"
              sx={{ width: 40 }}
            />
          ))}
        </Stack>

        {(resendCount < 2 && secondsLeft > 0) ? (
          <Typography align="center" variant="body2" mb={4} color="text.secondary">
            You can resend OTP in {secondsLeft}s
          </Typography>
        ) : (
          <Typography align="center" variant="body2" mb={4}>
            OTP not received?{" "}
            <Link href="#" onClick={handleResendOtp}>
              Send Again
            </Link>
          </Typography>
        )}

        {error && <Alert severity="error" >
          {errorMsg || "Error in Resend OTP. Please try later."}
        </Alert>}
      </Box>

      <Box>
        <Button
          onClick={handleVerifyOtp}
          fullWidth
          disabled={otp.join("").length !== 4}
          sx={{ background: otp.join("").length !== 4 && "#999 !important" }}
        >
          Verify Request
        </Button>
      </Box>
    </Box>
  )
}


export default ExistingContactOtp
